import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { fieldMappingsService } from '@/services/control/field-mappings-service';
import type {
  FieldMapping,
  FieldMappingInput,
} from '@/types/control/field-mapping';

import { adminMutationKeys, adminQueryKeys } from '@/hooks/admin/query-keys';

export type SaveFieldMappingArgs = {
  /** Absent for a create, present for an edit. */
  id?: string;
  input: FieldMappingInput;
};

/**
 * Create or update one mapping.
 *
 * Control's field mappings are read back from the same KV store the write goes
 * to, so unlike mesh's own read models this is read-after-write consistent and
 * needs no polling confirmation (`docs/eventual-consistency.md` covers the
 * cases that do). Invalidating the tenant's lists is enough.
 */
export function useSaveFieldMapping(
  tenantId: string
): UseMutationResult<FieldMapping, Error, SaveFieldMappingArgs> {
  const queryClient = useQueryClient();

  return useMutation<FieldMapping, Error, SaveFieldMappingArgs>({
    mutationKey: adminMutationKeys.saveFieldMapping(tenantId),
    mutationFn: ({ id, input }) =>
      id
        ? fieldMappingsService.update(tenantId, id, input)
        : fieldMappingsService.create(tenantId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.fieldMappingsByTenant(tenantId),
      }),
  });
}
